'use client';

import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { useEffect, useRef, useState, useMemo } from 'react';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { calculatePMC, calculateBikeTSS, DailyTSS } from '@/lib/pmc';
import { useTheme } from 'next-themes';
import { ParsedFitResult } from '@/lib/fitParser';
import { Bike, Footprints, Waves, Activity, Dumbbell, Flame } from 'lucide-react';
import { PowerProfileView } from '@/components/PowerProfileView';
import { AthleteSettings, DEFAULT_SETTINGS } from '@/lib/athleteSettings';

// ─── Shared tokens ──────────────────────────────────────────────────────────
const P = {
  blue:    '#3b82f6',
  teal:    '#14b8a6',
  cyan:    '#06b6d4',
  sky:     '#0ea5e9',
  emerald: '#10b981',
  rose:    '#f43f5e',
  amber:   '#f59e0b',
  violet:  '#8b5cf6',
  slate:   '#94a3b8',
};

function base(isDark: boolean) {
  return {
    text: isDark ? '#94a3b8' : '#6b7280',
    grid: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    tt:   { backgroundColor: isDark ? '#1c1c1e' : '#fff', borderColor: 'transparent', textStyle: { color: isDark ? '#f0f0f0' : '#1a1a1a', fontSize: 11 } },
  };
}

function xAxisConfig(dates: string[], textColour: string, gridColour: string) {
  const n = dates.length;
  const interval =
    n <= 14  ? 1  :
    n <= 35  ? 6  :
    n <= 90  ? 13 :
    n <= 180 ? 29 :
    n <= 365 ? 59 :
               89;
  const formatter = (val: string) => {
    const d = new Date(val);
    return n <= 180
      ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      : d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  };
  return {
    type: 'category' as const,
    boundaryGap: false,
    axisLine: { lineStyle: { color: gridColour } },
    axisTick: { show: false },
    axisLabel: { color: textColour, fontSize: 10, interval, formatter },
  };
}

// ─── Tiny helpers ───────────────────────────────────────────────────────────

function sport(s = '') {
  const l = s.toLowerCase();
  if (l.includes('run'))                      return { label: 'Run',  icon: <Footprints className="h-3.5 w-3.5" /> };
  if (l.includes('cycl') || l.includes('bik')) return { label: 'Ride', icon: <Bike       className="h-3.5 w-3.5" /> };
  if (l.includes('swim'))                     return { label: 'Swim', icon: <Waves       className="h-3.5 w-3.5" /> };
  if (l.includes('hiit') || l.includes('hyrox') || l.includes('condition')) return { label: 'Hyrox', icon: <Flame className="h-3.5 w-3.5" /> };
  if (l.includes('gym') || l.includes('strength')) return { label: 'Strength', icon: <Dumbbell className="h-3.5 w-3.5" /> };
  return { label: 'Workout', icon: <Activity className="h-3.5 w-3.5" /> };
}

function matchesSport(sportStr: string, filterStr: string) {
  if (!filterStr) return true;
  const sp = sportStr.toLowerCase();
  const sf = filterStr.toLowerCase();
  if (sf === 'cycl') return sp.includes('cycl') || sp.includes('bik');
  // 'other' = everything that is NOT a triathlon discipline
  if (sf === 'other') {
    return !sp.includes('run') &&
           !sp.includes('cycl') &&
           !sp.includes('bik') &&
           !sp.includes('swim');
  }
  return sp.includes(sf);
}

// ─── VO₂max estimation engine ────────────────────────────────────────────────

/**
 * Compute average of a numeric array, ignoring zeros.
 */
function meanNonZero(arr: number[]): number {
  const valid = arr.filter(v => v > 0);
  if (!valid.length) return 0;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}

/**
 * Estimate VO₂max from elevation telemetry: compute average gradient (%) from
 * the net elevation gain over distance — used to correct metabolic cost.
 */
function avgGradientPct(alt: number[], spd: number[]): number {
  if (alt.length < 2 || spd.length < 2) return 0;
  // Approximate total distance from speed stream (1 sample ≈ 1 s)
  const distM = spd.reduce((a, v) => a + (v > 0 ? v : 0), 0); // metres
  if (distM < 100) return 0;
  const elevGain = alt.reduce((acc, v, i) => {
    if (i === 0) return acc;
    const d = v - alt[i - 1];
    return acc + (d > 0.3 ? d : 0); // filter noise
  }, 0);
  return (elevGain / distM) * 100;
}

/**
 * Running VO₂max estimate.
 * Uses the ACSM Running equation: VO2 (mL/kg/min) = 3.5 + (0.2 × speed_m_min) + (0.9 × speed_m_min × grade_fraction)
 * Then solves for VO₂max using the %VO₂max ↔ %HRmax Karvonen relationship:
 *   %VO₂max ≈ 0.97 × %HRR  (Davies 1988, widely adopted)
 *   %HRR = (avgHR - rhr) / (hrMax - rhr)
 */
function estimateVo2maxRun(avgHR: number, avgSpeedMps: number, gradePct: number, settings: AthleteSettings): number | null {
  if (avgHR < 80 || avgSpeedMps < 0.5) return null;
  const { rhr, hrMax } = { rhr: 50, hrMax: settings.maxHr }; // approximate RHR if not provided, or add to settings? 
  // Wait, let's use a hardcoded 57 for RHR or add it? Let's just use 50.
  const hrr = Math.min(1, Math.max(0, (avgHR - 50) / (settings.maxHr - 50)));
  if (hrr < 0.3) return null; // too easy to estimate reliably
  const speedMmin = avgSpeedMps * 60; // m/s → m/min
  const grade = gradePct / 100;
  // ACSM: VO2 at this exercise intensity
  const vo2Ex = 3.5 + (0.2 * speedMmin) + (0.9 * speedMmin * grade);
  // %VO₂max from %HRR
  const pctVo2 = hrr * 0.97;
  if (pctVo2 < 0.1) return null;
  const est = vo2Ex / pctVo2;
  // Sanity clamp: 30–80 ml/kg/min
  return Math.min(80, Math.max(30, +est.toFixed(1)));
}

/**
 * Cycling VO₂max estimate (no power meter).
 * Uses the Åstrand-Ryhming nomogram adapted to HR reserve:
 *   VO2 (L/min) = workload (W) / 10.8 × (1 / (0.76 × %HRR + 0.07))  [Storer 1990 variant]
 * Workload is back-calculated from speed + gradient using the flat-road cycling equation:
 *   P_road (W) = (Crr × m × g + 0.5 × ρ × CdA × v²) × v
 * where:
 *   Crr = 0.004 (road tyre rolling resistance)
 *   CdA = 0.36 m² (hoods/road position)
 *   ρ   = 1.2 kg/m³ (air density)
 *   m   = rider + bike mass (97 kg)
 *   g   = 9.81 m/s²
 */
function estimateVo2maxCycle(avgHR: number, avgSpeedMps: number, gradePct: number, settings: AthleteSettings): number | null {
  if (avgHR < 80 || avgSpeedMps < 1) return null;
  const { maxHr, weight } = settings;
  const hrr = Math.min(1, Math.max(0, (avgHR - 50) / (maxHr - 50)));
  if (hrr < 0.25) return null;

  const totalMass = weight + 5; // rider + bike kg
  const g = 9.81, Crr = 0.004, CdA = 0.36, rho = 1.2;
  const grade = gradePct / 100;

  // Mechanical power estimate
  const F_roll = Crr * totalMass * g;
  const F_grav = totalMass * g * grade;
  const F_aero = 0.5 * rho * CdA * avgSpeedMps * avgSpeedMps;
  const power = Math.max(0, (F_roll + F_grav + F_aero) * avgSpeedMps);

  if (power < 20) return null; // too low to estimate

  // Storer/Åstrand: VO2 in L/min at this workload
  const vo2LMin = (power / 10.8) * (1 / (0.76 * hrr + 0.07));
  // Convert to mL/kg/min
  const vo2Rel = (vo2LMin * 1000) / weight;
  return Math.min(80, Math.max(28, +vo2Rel.toFixed(1)));
}

/**
 * Master estimator — picks the right formula per sport.
 * Returns null if estimation is not feasible.
 */
function estimateVo2maxFromActivity(w: import('@/lib/fitParser').ParsedFitResult, settings: AthleteSettings): number | null {
  const sp = (w.sport ?? '').toLowerCase();
  const isBike = sp.includes('cycl') || sp.includes('bik');
  const isRun  = sp.includes('run');
  if (!isBike && !isRun) return null;

  const { avgHeartRate, telemetry, totalDistance, totalDuration } = w;
  // Use session-level speed if telemetry is sparse
  const avgSpeedMps = totalDistance > 0 && totalDuration > 0
    ? totalDistance / totalDuration
    : meanNonZero(telemetry.speed);

  const gradePct = avgGradientPct(telemetry.altitude, telemetry.speed);

  if (isBike) return estimateVo2maxCycle(avgHeartRate, avgSpeedMps, gradePct, settings);
  return estimateVo2maxRun(avgHeartRate, avgSpeedMps, gradePct, settings);
}

function StatCard({ label, value, accent, sub }: { label: string; value: string | number; accent?: string; sub?: string }) {
  return (
    <div className="flex flex-col gap-0.5 sm:gap-1 p-3 sm:p-5 rounded-2xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)]">
      <span className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <span className="text-xl sm:text-3xl font-semibold tracking-tight" style={{ color: accent }}>{value}</span>
      {sub && <span className="text-[10px] sm:text-xs text-muted-foreground">{sub}</span>}
    </div>
  );
}

function ChartCard({ title, desc, children, span = '' }: { title: string; desc?: string; children: React.ReactNode; span?: string }) {
  return (
    <div className={`flex flex-col rounded-2xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)] overflow-hidden ${span}`}>
      <div className="px-5 pt-5 pb-4 border-b border-border/40">
        <p className="text-sm font-semibold tracking-tight">{title}</p>
        {desc && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>}
      </div>
      <div className="p-5 flex-1">{children}</div>
    </div>
  );
}

// ─── Mock data (shown until real data arrives) ──────────────────────────────
const MOCK_T  = Array.from({ length: 3600 }, (_, i) => i);
const MOCK_P  = MOCK_T.map(t => 200 + Math.sin(t / 100) * 50 + Math.abs(Math.sin(t * 1.23)) * 20);
const MOCK_HR = MOCK_T.map(t => 140 + Math.sin(t / 200) * 10 + Math.abs(Math.sin(t * 0.83)) * 5);
const MOCK_SP = MOCK_T.map(t => (3.2 + Math.sin(t / 150) * 0.4) * 3.6);

// ─── Component ───────────────────────────────────────────────────────────────
export function GoldenCheetahWorkoutView({
  realData,
  metricsData = [],
  sportFilter,
  hideAggregates = false,
  athleteSettings = DEFAULT_SETTINGS,
}: {
  realData?: ParsedFitResult[] | null;
  metricsData?: import('@/lib/pbSync').ActivityMetric[];
  sportFilter?: string;
  hideAggregates?: boolean;
  athleteSettings?: AthleteSettings;
}) {
  const powerRef = useRef<ReactECharts>(null);
  const hrRef    = useRef<ReactECharts>(null);
  const speedRef = useRef<ReactECharts>(null);
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState('0');

  useEffect(() => {
    setMounted(true);
    echarts.connect('wg');
  }, []);

  // Sync groups natively since ReactECharts doesn't take a 'group' prop
  useEffect(() => {
    if (powerRef.current) powerRef.current.getEchartsInstance().group = 'wg';
    if (hrRef.current)    hrRef.current.getEchartsInstance().group = 'wg';
    if (speedRef.current) speedRef.current.getEchartsInstance().group = 'wg';
  });

  // Aggregates over the time window
  const aggregates = useMemo(() => {
    let filteredData = realData || [];
    if (sportFilter) {
      filteredData = filteredData.filter(d => matchesSport(d.sport || '', sportFilter));
    }
    const distTotal = filteredData.reduce((a, b) => a + ((b as any).distance || 0), 0) / 1000;
    const durTotal  = filteredData.reduce((a, b) => a + ((b as any).duration || 0), 0);
    const kjTotal   = filteredData.reduce((a, b) => a + ((b as any).totalWork || 0), 0);
    const active    = filteredData.filter(d => (d as any).duration > 0).length;
    return { distTotal, durTotal, kjTotal, active };
  }, [realData, sportFilter]);

  const processed = useMemo(() => {
    let filteredData = realData || [];
    if (sportFilter) {
      filteredData = filteredData.filter(d => matchesSport(d.sport || '', sportFilter));
    }
    return [...filteredData].sort((a, b) => b.startTime.getTime() - a.startTime.getTime());
  }, [realData, sportFilter]);

  const isDark = mounted ? resolvedTheme === 'dark' : true;
  const { text, grid, tt } = base(isDark);

  const telemetryWorkouts = useMemo(() => {
    if (!realData) return [];
    let valid = realData;
    if (sportFilter) {
      valid = valid.filter(d => matchesSport(d.sport || '', sportFilter));
    }
    return valid
      .filter(w => w.telemetry?.timestamps?.length > 0)
      .sort((a, b) => b.startTime.getTime() - a.startTime.getTime());
  }, [realData, sportFilter]);

  // Active streams
  const { ts, power, hr, speed, cadence } = useMemo(() => {
    if (telemetryWorkouts.length > 0) {
      const w = telemetryWorkouts[Math.min(parseInt(selectedIdx) || 0, telemetryWorkouts.length - 1)];
      return { ts: w.telemetry.timestamps, power: w.telemetry.power, hr: w.telemetry.heart_rate, speed: w.telemetry.speed, cadence: w.telemetry.cadence || [] };
    }
    return { ts: MOCK_T, power: MOCK_P, hr: MOCK_HR, speed: MOCK_SP, cadence: [] };
  }, [telemetryWorkouts, selectedIdx]);

  // PMC from real data
  const { pmcData, ctlNow, atlNow, tsbNow, sportTotals, z12, z3, z45, rampDates, rampVals } = useMemo(() => {
    if (!realData) return { pmcData: [], ctlNow: 0, atlNow: 0, tsbNow: 0, sportTotals: { swim: 0, ride: 0, run: 0, hyrox: 0, strength: 0, other: 0 }, z12: 0, z3: 0, z45: 0, rampDates: [], rampVals: [] };
    let filteredData = realData;
    if (sportFilter) {
      filteredData = filteredData.filter(d => matchesSport(d.sport || '', sportFilter));
    }
    const tssArr = filteredData.map(d => ({
      date: d.startTime.toISOString().split('T')[0],
      tss: (d as any).trainingLoad,
      type: d.sport || (d as any).type || 'unknown'
    }));
    const today = new Date();

    // Derive span from actual data
    let spanStart = new Date(today);
    spanStart.setDate(today.getDate() - 59);
    if (realData && realData.length > 0) {
      const earliest = realData.reduce((min, w) =>
        w.startTime < min ? w.startTime : min, realData[0].startTime);
      spanStart = new Date(earliest);
    }

    const dates: string[] = [];
    for (const d = new Date(spanStart); d <= today; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split('T')[0]);
    }

    const xInterval = Math.max(1, Math.floor(dates.length / 7));
    const tssMap = new Map<string, number>();
    const totals = { swim: 0, ride: 0, run: 0, hyrox: 0, strength: 0, other: 0 };

    if (realData) {
      realData.forEach(w => {
        const key = w.startTime.toISOString().split('T')[0];
        let tss = 0;
        if (w.normalizedPower) tss = calculateBikeTSS(w.totalDuration, w.normalizedPower, athleteSettings.ftp);
        else if (w.avgHeartRate > 100) tss = (w.totalDuration / 3600) * ((w.avgHeartRate - 100) / 100) * 100;
        else tss = (w.totalDuration / 3600) * 50;
        tssMap.set(key, (tssMap.get(key) || 0) + tss);
        const hr = (w.totalDuration / 3600);
        const sp = w.sport?.toLowerCase() || '';
        if (sp.includes('run')) totals.run += hr;
        else if (sp.includes('cycl') || sp.includes('bik')) totals.ride += hr;
        else if (sp.includes('swim')) totals.swim += hr;
        else if (sp.includes('hiit') || sp.includes('hyrox') || sp.includes('condition')) totals.hyrox += hr;
        else if (sp.includes('gym') || sp.includes('strength')) totals.strength += hr;
        else totals.other += hr;
      });
    } else {
      // demo seed
      dates.forEach((d, i) => tssMap.set(d, Math.abs(Math.sin(i * 0.45)) * 100));
      totals.swim = 3; totals.ride = 8; totals.run = 5; totals.hyrox = 2; totals.strength = 1;
    }

    const input: DailyTSS[] = dates.map(d => ({ date: d, tss: tssMap.get(d) || 0 }));
    const pmc  = calculatePMC(input, 38, 28);
    const last = pmc[pmc.length - 1];

    // Zone distribution — sport-aware, across all filtered activities
    let z12 = 0, z3 = 0, z45 = 0;
    if (realData) {
      realData.forEach(w => {
        const sp = (w.sport ?? '').toLowerCase();
        const isBike = sp.includes('cycl') || sp.includes('bik');
        if (isBike && w.normalizedPower) {
          if      (w.normalizedPower < athleteSettings.ftp * 0.80) z12++;
          else if (w.normalizedPower < athleteSettings.ftp * 0.95) z3++;
          else                                      z45++;
        } else if (w.avgHeartRate > 0) {
          if      (w.avgHeartRate < 181 * 0.82) z12++;
          else if (w.avgHeartRate < 181 * 0.94) z3++;
          else                                   z45++;
        }
      });
    } else { z12 = 74; z3 = 16; z45 = 10; }

    // Weekly ramp rate (CTL delta week-over-week)
    const rampDates: string[] = [];
    const rampVals:  number[] = [];
    for (let i = 7; i < pmc.length; i += 7) {
      rampDates.push(pmc[i].date);
      rampVals.push(+(pmc[i].ctl - pmc[i - 7].ctl).toFixed(1));
    }

    return { pmcData: pmc, ctlNow: last?.ctl ?? 0, atlNow: last?.atl ?? 0, tsbNow: last?.tsb ?? 0, sportTotals: totals, z12, z3, z45, rampDates, rampVals };
  }, [realData, athleteSettings]);

  // MMP curve
  const mmpDurs = [1, 5, 10, 30, 60, 300, 600, 1200];
  const mmpLabels = ['1s', '5s', '10s', '30s', '1m', '5m', '10m', '20m'];
  const mmpVals = mmpDurs.map(d => {
    if (power.length < d) return 0;
    let sum = power.slice(0, d).reduce((a, b) => a + b, 0), best = sum;
    for (let i = d; i < power.length; i++) { sum += power[i] - power[i - d]; if (sum > best) best = sum; }
    return Math.round(best / d);
  });

  // Detect sport from selected workout (not from avg power)
  const selectedWorkout = telemetryWorkouts.length > 0
    ? telemetryWorkouts[Math.min(parseInt(selectedIdx) || 0, telemetryWorkouts.length - 1)]
    : null;
  const selectedSport = (selectedWorkout?.sport ?? '').toLowerCase();
  const isRide = selectedSport.includes('cycl') || selectedSport.includes('bik');
  const isRun  = selectedSport.includes('run');
  const isSwim = selectedSport.includes('swim');
  // Default to HR zones for swim/unknown; power zones only when we have a ride with power data
  const usePowerZones = isRide && power.some(p => p > 10);

  // Power zones (cycling only, 6 zones based on athleteSettings.ftp)
  const POWER_ZONE_BOUNDS = [0.55, 0.75, 0.90, 1.05, 1.20].map(f => athleteSettings.ftp * f);
  const pzCounts = [0, 0, 0, 0, 0, 0];
  if (usePowerZones) {
    power.forEach(p => {
      if (p < POWER_ZONE_BOUNDS[0]) pzCounts[0]++;
      else if (p < POWER_ZONE_BOUNDS[1]) pzCounts[1]++;
      else if (p < POWER_ZONE_BOUNDS[2]) pzCounts[2]++;
      else if (p < POWER_ZONE_BOUNDS[3]) pzCounts[3]++;
      else if (p < POWER_ZONE_BOUNDS[4]) pzCounts[4]++;
      else pzCounts[5]++;
    });
  }
  const pzMin = pzCounts.map(c => +(c / 60).toFixed(1));

  // HR zones (all sports, 5 zones based on athleteSettings.lthr)
  const HR_ZONE_BOUNDS = [0.82, 0.89, 0.94, 0.99].map(f => athleteSettings.lthr * f);
  const hzCounts = [0, 0, 0, 0, 0];
  hr.forEach(v => {
    if (v < 1) return; // skip zeros
    if (v < HR_ZONE_BOUNDS[0]) hzCounts[0]++;
    else if (v < HR_ZONE_BOUNDS[1]) hzCounts[1]++;
    else if (v < HR_ZONE_BOUNDS[2]) hzCounts[2]++;
    else if (v < HR_ZONE_BOUNDS[3]) hzCounts[3]++;
    else hzCounts[4]++;
  });
  const hzMin = hzCounts.map(c => +(c / 60).toFixed(1));

  if (!mounted) return null;

  // ── ECharts options ────────────────────────────────────────────────────────

  const axisCommon = { axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: text, fontSize: 10 }, splitLine: { lineStyle: { color: grid, type: 'dashed' as const } } };
  const gridCommon = { left: 0, right: 0, bottom: 24, top: 8, containLabel: true };

  const lineOpt = (dataArr: number[], colour: string, unit: string, label: string, invertY = false) => ({
    backgroundColor: 'transparent',
    textStyle: { color: text, fontFamily: 'Inter, sans-serif', fontSize: 11 },
    tooltip: { trigger: 'axis', ...tt },
    grid: gridCommon,
    xAxis: { type: 'category', data: ts, boundaryGap: false, axisLine: { lineStyle: { color: grid } }, axisTick: { show: false }, axisLabel: { color: text, fontSize: 10, interval: Math.max(1, Math.floor(ts.length / 8)) } },
    yAxis: { type: 'value', name: unit, inverse: invertY, ...axisCommon },
    dataZoom: [{ type: 'inside' }, { type: 'slider', height: 18, handleSize: '80%', textStyle: { color: text, fontSize: 9 }, borderColor: 'transparent', fillerColor: `${colour}20` }],
    series: [{ name: label, type: 'line', data: dataArr, showSymbol: false, smooth: 0.2, lineStyle: { color: colour, width: 1.5 }, itemStyle: { color: colour }, areaStyle: { origin: invertY ? 'end' : 'auto', color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: `${colour}30` }, { offset: 1, color: `${colour}00` }]) } }],
  });

  const pmcOpt = {
    backgroundColor: 'transparent',
    textStyle: { color: text, fontFamily: 'Inter, sans-serif', fontSize: 11 },
    tooltip: { trigger: 'axis', ...tt },
    legend: { data: ['Fitness', 'Fatigue', 'Form'], textStyle: { color: text, fontSize: 11 }, bottom: 0, itemWidth: 12, itemHeight: 12 },
    grid: { left: 0, right: 0, bottom: 32, top: 8, containLabel: true },
    xAxis: { ...xAxisConfig(pmcData.map(d => d.date), text, grid), data: pmcData.map(d => d.date) },
    yAxis: [
      { type: 'value', ...axisCommon },
      { type: 'value', splitLine: { show: false }, axisLabel: { show: false }, axisLine: { show: false }, axisTick: { show: false } },
    ],
    series: [
      { name: 'Fitness', type: 'line', data: pmcData.map(d => d.ctl), showSymbol: false, smooth: 0.3, lineStyle: { color: P.blue, width: 2 }, itemStyle: { color: P.blue }, areaStyle: { color: `${P.blue}18` } },
      { name: 'Fatigue', type: 'line', data: pmcData.map(d => d.atl), showSymbol: false, smooth: 0.3, lineStyle: { color: P.rose, width: 2 }, itemStyle: { color: P.rose } },
      { name: 'Form',    type: 'line', data: pmcData.map(d => d.tsb), showSymbol: false, smooth: 0.3, lineStyle: { color: P.amber, width: 2, type: 'dashed' }, itemStyle: { color: P.amber } },
      { name: 'Daily', type: 'bar', yAxisIndex: 1, data: pmcData.map(d => d.tss), itemStyle: { color: `${P.slate}30`, borderRadius: [2, 2, 0, 0] }, barMaxWidth: 8 },
    ],
  };

  const mmpOpt = {
    backgroundColor: 'transparent',
    textStyle: { color: text, fontFamily: 'Inter, sans-serif', fontSize: 11 },
    tooltip: { trigger: 'axis', formatter: '{b}: {c} W', ...tt },
    grid: gridCommon,
    xAxis: { type: 'category', data: mmpLabels, boundaryGap: false, axisLine: { lineStyle: { color: grid } }, axisTick: { show: false }, axisLabel: { color: text, fontSize: 10 } },
    yAxis: { type: 'value', name: 'W', ...axisCommon },
    series: [{ name: 'MMP', type: 'line', data: mmpVals, smooth: true, symbolSize: 6,
      lineStyle: { color: P.amber, width: 2 }, itemStyle: { color: P.amber },
      areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: `${P.amber}40` }, { offset: 1, color: `${P.amber}00` }]) } }],
  };

  const ZONE_COLS = [P.teal, P.sky, P.amber, P.rose, '#dc2626', '#7f1d1d'];
  const pzOpt = {
    backgroundColor: 'transparent',
    textStyle: { color: text, fontFamily: 'Inter, sans-serif', fontSize: 11 },
    tooltip: { trigger: 'axis', formatter: '{b}: {c} min', ...tt },
    grid: gridCommon,
    xAxis: { type: 'category', data: ['Z1', 'Z2', 'Z3', 'Z4', 'Z5', 'Z6'], axisLine: { lineStyle: { color: grid } }, axisTick: { show: false }, axisLabel: { color: text, fontSize: 10 } },
    yAxis: { type: 'value', name: 'min', ...axisCommon },
    series: [{ type: 'bar', data: pzMin.map((v, i) => ({ value: v, itemStyle: { color: ZONE_COLS[i], borderRadius: [3, 3, 0, 0] } })), barMaxWidth: 32 }],
  };

  const hrZoneOpt = {
    backgroundColor: 'transparent',
    textStyle: { color: text, fontFamily: 'Inter, sans-serif', fontSize: 11 },
    tooltip: { trigger: 'axis', formatter: '{b}: {c} min', ...tt },
    grid: gridCommon,
    xAxis: { type: 'category', data: ['Z1', 'Z2', 'Z3', 'Z4', 'Z5'], axisLine: { lineStyle: { color: grid } }, axisTick: { show: false }, axisLabel: { color: text, fontSize: 10 } },
    yAxis: { type: 'value', name: 'min', ...axisCommon },
    series: [{ type: 'bar', data: hzMin.map((v, i) => ({ value: v, itemStyle: { color: ZONE_COLS[i], borderRadius: [3, 3, 0, 0] } })), barMaxWidth: 32 }],
  };

  const disciplineOpt = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item', formatter: '{b}: {c} h ({d}%)', backgroundColor: isDark ? '#1c1c1e' : '#fff', borderColor: 'transparent', textStyle: { color: isDark ? '#f0f0f0' : '#1a1a1a', fontSize: 11 } },
    series: [{
      type: 'pie', radius: ['55%', '78%'], center: ['50%', '50%'],
      itemStyle: { borderRadius: 4, borderColor: isDark ? '#0a0a0a' : '#f5f5f7', borderWidth: 3 },
      label: { show: false },
      data: [
        { value: +(sportTotals.swim).toFixed(1), name: 'Swim', itemStyle: { color: P.cyan } },
        { value: +(sportTotals.ride).toFixed(1), name: 'Ride', itemStyle: { color: P.blue } },
        { value: +(sportTotals.run).toFixed(1),  name: 'Run',  itemStyle: { color: P.emerald } },
        { value: +(sportTotals.hyrox).toFixed(1),  name: 'Hyrox', itemStyle: { color: P.amber } },
        { value: +(sportTotals.strength).toFixed(1), name: 'Strength', itemStyle: { color: P.violet } },
      ].filter(d => d.value > 0),
    }],
  };

  // Polarisation donut (all sessions in range)
  const polOpt = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item', formatter: '{b}: {d}%', ...tt },
    series: [{ type: 'pie', radius: ['55%', '78%'], center: ['50%', '50%'],
      itemStyle: { borderRadius: 4, borderColor: isDark ? '#0a0a0a' : '#f5f5f7', borderWidth: 3 }, label: { show: false },
      data: [
        { value: z12, name: 'Zone 1–2', itemStyle: { color: P.teal } },
        { value: z3,  name: 'Zone 3',   itemStyle: { color: P.amber } },
        { value: z45, name: 'Zone 4–5', itemStyle: { color: P.rose  } },
      ],
    }],
  };

  // Ramp rate bars
  const baseOpt = { backgroundColor: 'transparent', textStyle: { color: text, fontFamily: 'Inter, sans-serif', fontSize: 11 } };
  const rampOpt = {
    ...baseOpt, tooltip: { trigger: 'axis', formatter: '{b}: {c} pts/wk', ...tt },
    grid: gridCommon,
    xAxis: { ...xAxisConfig(rampDates, text, grid), data: rampDates, boundaryGap: true },
    yAxis: { type: 'value', name: 'CTL Δ', ...axisCommon },
    series: [{ type: 'bar', data: rampVals,
      itemStyle: { color: (p: any) => p.value > 8 ? P.rose : p.value < 0 ? P.sky : P.teal, borderRadius: [3, 3, 0, 0] },
      barMaxWidth: 24,
      markArea: {
        data: [
          [{ yAxis: 3, itemStyle: { color: `${P.teal}15` } }, { yAxis: 8 }]
        ]
      }
    }],
  };

  // Continuous time axis
  const allDates = pmcData.map(d => d.date);

  // VO₂max trend — measured (PocketBase) with estimated fallback
  // Step 1: build map of measured values from PocketBase
  const vo2MeasuredMap = new Map(metricsData.filter(m => m.vo2max != null).map(m => [m.date, m.vo2max!]));

  // Step 2: for every activity that has NO measured VO2, compute an estimate
  const vo2EstMap = new Map<string, number>();
  if (realData) {
    realData.forEach(w => {
      const key = w.startTime.toISOString().split('T')[0];
      if (!vo2MeasuredMap.has(key)) {
        // Only estimate if we have enough data (duration > 5 min, HR present)
        if (w.totalDuration > 300 && w.avgHeartRate > 80) {
          const est = estimateVo2maxFromActivity(w, athleteSettings);
          if (est !== null) {
            // If multiple activities on the same day, take the max estimate (peak effort)
            const existing = vo2EstMap.get(key);
            if (existing == null || est > existing) vo2EstMap.set(key, est);
          }
        }
      }
    });
  }

  // Step 3: forward-fill on the date axis — measured values override estimates
  let lastVo2Measured: number | null = null;
  let lastVo2Est: number | null = null;
  const vo2DataMeasured: (number | null)[] = [];
  const vo2DataEst: (number | null)[] = [];

  allDates.forEach(d => {
    if (vo2MeasuredMap.has(d)) lastVo2Measured = vo2MeasuredMap.get(d)!;
    vo2DataMeasured.push(lastVo2Measured);

    // Estimated: only plot on days where we have a new estimate (no forward-fill)
    vo2DataEst.push(vo2EstMap.has(d) ? vo2EstMap.get(d)! : null);
  });

  const hasVo2 = vo2DataMeasured.some(v => v !== null) || vo2DataEst.some(v => v !== null);

  const vo2Opt = {
    ...baseOpt,
    tooltip: {
      trigger: 'axis', ...tt,
      formatter: (params: any[]) => {
        const date = params[0]?.axisValue ?? '';
        const lines = params
          .filter((p: any) => p.value !== null && p.value !== undefined)
          .map((p: any) => {
            const tag = p.seriesName === 'Measured' ? '' : ' <span style="font-size:9px;opacity:0.7">(est)</span>';
            return `${p.marker}${p.seriesName}${tag}: <b>${p.value} ml/kg/min</b>`;
          });
        return lines.length ? `<b>${date}</b><br/>${lines.join('<br/>')}` : '';
      },
    },
    grid: gridCommon,
    xAxis: { ...xAxisConfig(allDates, text, grid), data: allDates },
    yAxis: { type: 'value', name: 'ml/kg/min', ...axisCommon, min: (v: any) => Math.max(30, v.min - 3) },
    series: [
      {
        name: 'Measured',
        type: 'line',
        data: vo2DataMeasured,
        connectNulls: true,
        smooth: 0.4,
        showSymbol: true,
        symbolSize: 7,
        lineStyle: { color: P.teal, width: 2.5 },
        itemStyle: { color: P.teal },
        areaStyle: { color: `${P.teal}22` },
      },
      {
        name: 'Estimated',
        type: 'scatter',
        data: vo2DataEst,
        symbolSize: 6,
        itemStyle: { color: P.amber, opacity: 0.85, borderColor: P.amber, borderWidth: 1 },
        symbol: 'diamond',
      },
    ],
  };

  // Efficiency Factor (NP ÷ avg HR)
  const efMap = new Map<string, number>();
  if (realData) {
    realData.forEach(w => {
      if (w.normalizedPower && w.avgHeartRate && w.sport.toLowerCase().includes('cycl')) {
        const dateStr = w.startTime.toISOString().split('T')[0];
        efMap.set(dateStr, +(w.normalizedPower / w.avgHeartRate).toFixed(3));
      }
    });
  }
  const efData = allDates.map(d => efMap.get(d) ?? null);
  const hasEF = efMap.size > 0;
  
  const efOpt = {
    ...baseOpt, tooltip: { trigger: 'axis', formatter: '{b}: {c}', ...tt },
    grid: gridCommon,
    xAxis: { ...xAxisConfig(allDates, text, grid), data: allDates },
    yAxis: { type: 'value', name: 'NP/HR', ...axisCommon, min: (v: any) => Math.max(0, v.min - 0.1) },
    series: [{ name: 'EF', type: 'scatter', symbolSize: 7, data: efData,
      itemStyle: { color: P.sky, opacity: 0.75 } }],
  };


  // Aerobic efficiency scatter — sport-aware
  // For rides: power vs HR. For runs: speed vs HR. Requires non-empty streams.
  const hasSpeed = speed.some(s => s > 0);
  const hasPower = power.some(p => p > 10);
  const showSpeedScatter = (isRun || !hasPower) && hasSpeed;
  const scatterData: [number, number][] = [];

  for (let i = 0; i < hr.length; i++) {
    if (hr[i] < 80) continue;
    if (showSpeedScatter && i < speed.length && speed[i] > 1) {
      scatterData.push([+(speed[i] * 3.6).toFixed(1), hr[i]]);
    } else if (!showSpeedScatter && i < power.length && power[i] > 20) {
      scatterData.push([power[i], hr[i]]);
    }
  }

  const scatterXLabel = showSpeedScatter ? 'Speed (km/h)' : 'Power (W)';
  const noScatterData = scatterData.length === 0;

  const scatterOpt = {
    backgroundColor: 'transparent',
    textStyle: { color: text, fontFamily: 'Inter, sans-serif', fontSize: 11 },
    tooltip: { trigger: 'item', formatter: (p: any) => `HR: ${p.value[1]} bpm<br/>${scatterXLabel}: ${p.value[0]}`, ...tt },
    grid: gridCommon,
    xAxis: { type: 'value', name: scatterXLabel, ...axisCommon, splitLine: { lineStyle: { color: grid, type: 'dashed' as const } } },
    yAxis: { type: 'value', name: 'bpm', min: (v: any) => Math.max(70, v.min - 10), ...axisCommon },
    series: [{ type: 'scatter', symbolSize: 4, data: scatterData, itemStyle: { color: P.blue, opacity: 0.35 } }],
  };

  return (
    <div className="space-y-10">

      {!hideAggregates && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Fitness (CTL)"  value={Math.round(ctlNow)} sub="42-day load" accent={P.blue} />
            <StatCard label="Fatigue (ATL)"  value={Math.round(atlNow)} sub="7-day load"  accent={P.rose} />
            <StatCard label="Form (TSB)"     value={`${tsbNow > 0 ? '+' : ''}${Math.round(tsbNow)}`} accent={tsbNow >= 0 ? P.teal : P.amber} />
            <StatCard label="Activities"     value={realData?.length ?? 0} />
          </div>

          {/* Training distribution: polarisation + ramp + discipline */}
          <div>
        <h2 className="text-base font-semibold tracking-tight">Training distribution</h2>
        <p className="text-sm text-muted-foreground mt-0.5 mb-4">How you split effort across intensities, sports, and time.</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ChartCard title="Intensity split" desc="Target: 75-80% low intensity. Sport-aware — rides use power, runs use HR.">
            <div className="flex flex-col items-center gap-3">
              <ReactECharts option={polOpt} style={{ height: 180, width: '100%' }} />
              <div className="flex gap-4 text-xs text-muted-foreground">
                <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: P.teal }} />Zone 1–2</span>
                <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: P.amber }} />Zone 3</span>
                <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: P.rose }} />Zone 4–5</span>
              </div>
            </div>
          </ChartCard>
          <ChartCard title="Discipline split" desc="Training hours by sport.">
            <div className="flex flex-col items-center gap-3">
              <ReactECharts option={disciplineOpt} style={{ height: 180, width: '100%' }} />
              <div className="flex gap-4 text-xs text-muted-foreground">
                <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: P.cyan }} />Swim</span>
                <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: P.blue }} />Ride</span>
                <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: P.emerald }} />Run</span>
                <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: P.amber }} />Hyrox</span>
                <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: P.violet }} />Strength</span>
              </div>
            </div>
          </ChartCard>
          <ChartCard title="Weekly fitness ramp" desc="Safe range: +3 to +8 pts/wk (Teal band). Sustained values above +10 drive overuse injury.">
            <ReactECharts option={rampOpt} style={{ height: 220 }} />
          </ChartCard>
        </div>
      </div>

      {/* Aerobic adaptation */}
      <div>
        <h2 className="text-base font-semibold tracking-tight">Aerobic adaptation</h2>
        <p className="text-sm text-muted-foreground mt-0.5 mb-4">Long-term markers of physiological progress.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="VO₂max trend" desc="Teal line: Garmin-measured VO₂max (forward-filled). Amber diamonds: physics-estimated from speed, HR & elevation on days without a Garmin reading.">
            {hasVo2
              ? <ReactECharts option={vo2Opt} style={{ height: 220 }} />
              : <div className="flex items-center justify-center h-40 text-xs text-muted-foreground">No VO₂max data — sync from PocketBase to populate.</div>
            }
          </ChartCard>
          <ChartCard title="Efficiency factor (NP ÷ HR)" desc="A rising EF means the same power output at a lower heart rate — the gold-standard cycling economy metric.">
            {hasEF
              ? <ReactECharts option={efOpt} style={{ height: 220 }} />
              : <div className="flex items-center justify-center h-40 text-xs text-muted-foreground">Requires normalised power and HR per session.</div>
            }
          </ChartCard>
        </div>
      </div>
      </>
      )}

      {sportFilter === 'cycl' && (
        <div className="mb-10">
          <h2 className="text-base font-semibold tracking-tight mb-4">Power Profile</h2>
          <PowerProfileView realData={realData} />
        </div>
      )}

      {/* Workout selector */}
      <div className="space-y-3">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Activity inspector</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Select a session to load its telemetry streams.</p>
        </div>
        {telemetryWorkouts.length > 0 && (
          <ScrollArea className="w-full">
            <div className="flex gap-2 pb-2">
              {telemetryWorkouts.map((w, idx) => {
                const { label, icon } = sport(w.sport);
                const active = selectedIdx === idx.toString();
                return (
                  <button
                    key={idx}
                    onClick={() => setSelectedIdx(idx.toString())}
                    className={`flex-shrink-0 flex flex-col text-left gap-1 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border text-xs transition-all duration-150 w-36 sm:w-44
                      ${active
                        ? 'bg-foreground text-background border-foreground'
                        : 'bg-card border-border/60 text-muted-foreground hover:border-foreground/30 hover:text-foreground'
                      }`}
                  >
                    <div className="flex items-center justify-between w-full font-medium">
                      <span>{w.startTime.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                      <div className="flex items-center gap-1">{icon}<span>{label}</span></div>
                    </div>
                    <span className="text-lg font-semibold tracking-tight">{Math.round(w.totalDuration / 60)}<span className="text-xs font-normal ml-0.5">min</span></span>
                    {w.normalizedPower && <span className="opacity-70">NP {Math.round(w.normalizedPower)}W · HR {Math.round(w.avgHeartRate)}</span>}
                  </button>
                );
              })}
            </div>
            <ScrollBar orientation="horizontal" className="opacity-40" />
          </ScrollArea>
        )}
      </div>

      {/* Telemetry streams */}
      <div className="grid grid-cols-1 gap-4">
        {isRide && (
          <ChartCard title="Power" desc="Watts over time. Scroll or pinch to zoom.">
            <ReactECharts ref={powerRef} option={lineOpt(power, P.amber, 'W', 'Power')} style={{ height: 200 }} />
          </ChartCard>
        )}
        <ChartCard title="Heart rate" desc="BPM over time. Synced with other charts.">
          <ReactECharts ref={hrRef} option={lineOpt(hr, P.rose, 'bpm', 'HR')} style={{ height: 200 }} />
        </ChartCard>
        {isRide ? (
          <ChartCard title="Speed" desc="km/h over time.">
            <ReactECharts ref={speedRef} option={lineOpt(speed.map(s => +(s * 3.6).toFixed(1)), P.sky, 'km/h', 'Speed')} style={{ height: 200 }} />
          </ChartCard>
        ) : isRun ? (
          <ChartCard title="Pace" desc="min/km over time.">
            <ReactECharts ref={speedRef} option={lineOpt(speed.map(s => s > 0 ? +(16.6667 / s).toFixed(2) : 0), P.sky, 'min/km', 'Pace', true)} style={{ height: 200 }} />
          </ChartCard>
        ) : (
          <ChartCard title="Pace" desc="min/100m over time.">
            <ReactECharts ref={speedRef} option={lineOpt(speed.map(s => s > 0 ? +(1.66667 / s).toFixed(2) : 0), P.sky, 'min/100m', 'Pace', true)} style={{ height: 200 }} />
          </ChartCard>
        )}
        {cadence && cadence.some(c => c > 0) && (
          <ChartCard title={isRide ? "Cadence" : isRun ? "Cadence" : "Stroke Rate"} desc={isRide ? "rpm over time." : isRun ? "spm over time." : "strokes/min over time."}>
            <ReactECharts option={lineOpt(cadence, P.emerald, isRide ? 'rpm' : isRun ? 'spm' : 'spm', 'Cadence')} style={{ height: 200 }} />
          </ChartCard>
        )}
      </div>

      {/* Advanced panels */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {isRide && (
          <>
            <ChartCard title="Peak power curve" desc="Best average power at each duration.">
              <ReactECharts option={mmpOpt} style={{ height: 220 }} />
            </ChartCard>
            <ChartCard
              title={`Power zones — athleteSettings.ftp ${athleteSettings.ftp} W`}
              desc="Time in each cycling power zone."
            >
              {usePowerZones
                ? <ReactECharts option={pzOpt} style={{ height: 220 }} />
                : <div className="flex items-center justify-center h-40 text-xs text-muted-foreground">Select a ride with power data.</div>
              }
            </ChartCard>
          </>
        )}
        <ChartCard title={`HR zones — athleteSettings.lthr ${athleteSettings.lthr} bpm`} desc="Time in each heart rate zone. Valid for all sports.">
          <ReactECharts option={hrZoneOpt} style={{ height: 220 }} />
        </ChartCard>
        <ChartCard
          title="Aerobic efficiency"
          desc={noScatterData
            ? 'No telemetry available for this session.'
            : `${scatterXLabel} vs HR — a tighter, right-shifted cluster means better aerobic economy.`
          }
        >
          {noScatterData
            ? <div className="flex items-center justify-center h-40 text-xs text-muted-foreground">Requires HR + {isRun ? 'GPS speed' : 'power'} streams.</div>
            : <ReactECharts option={scatterOpt} style={{ height: 220 }} />
          }
        </ChartCard>
      </div>


    </div>
  );
}
